/* ============================================================================
   McDuffy Quiz v2 — ENGINE (pure state machine)
   ----------------------------------------------------------------------------
   No DOM. No fetch. Fully unit-testable in node. Owns: state, the dynamic
   multi-dog FLOW, the name+pronoun+neutered/spayed lexicon, validation,
   per-question progress, back/goto (answers retained), and the client-side
   recipe recommendation (server stays the source of truth for the reveal).

   Depends on MQ_DATA + MQ_SCREENS (injected for node; globals in browser).
   Browser global `window.MQ_Engine`; CommonJS export for tests.
   ============================================================================ */
(function (root, factory) {
  var api = factory(
    (typeof require === 'function') ? require('./mcduffy-quiz-v2.data.js') : root.MQ_DATA,
    (typeof require === 'function') ? require('./mcduffy-quiz-v2.screens.js') : root.MQ_SCREENS
  );
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.MQ_Engine = api;
})(typeof window !== 'undefined' ? window : this, function (DATA, SCREENS) {
  'use strict';

  /* ---------- small pure helpers ---------- */
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }
  function cap(s) { s = String(s || ''); return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  function possessive(name) { name = String(name || ''); if (!name) return ''; return name + '’s'; }
  function pluralBags(n) { return (Number(n) === 1) ? '1 bag' : (n + ' bags'); }
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var LB_TO_KG = 0.45359237;

  function makeDog() {
    return {
      name: '',
      sex: null, ageYears: null, ageMonths: 0, neutered: null,
      breed: { type: null, breeds: [], sizeFallback: null },
      weight: null, weightUnit: 'kg', weightEstimated: false,
      sensitivities: [], excludedAllergens: [], hypoallergenic: false,
      avoidHas: null, avoidPreferences: [],
      healthGoals: [],
      activityTier: null, workingDog: false,
      weightGoal: null,
      currentFood: null, currentFoodLabel: '', currentBrand: '', brandNotListed: false,
      flavors: []
    };
  }

  /* ---------- lexicon / tokens ---------- */
  function pronounSet(sex) {
    if (sex === 'boy') return { subj: 'he', obj: 'him', poss: 'his', neuter: 'neutered' };
    if (sex === 'girl') return { subj: 'she', obj: 'her', poss: 'her', neuter: 'spayed' };
    return { subj: 'they', obj: 'them', poss: 'their', neuter: 'spayed or neutered' };
  }

  /* ---------- engine factory ---------- */
  function createEngine(opts) {
    opts = opts || {};
    var subscribers = [];
    var requestedFlowMode = opts.flowMode === 'ips2026' ? 'ips2026' : 'standard';

    var state = {
      sessionId: opts.sessionId || ('mq_' + Math.random().toString(36).slice(2) + Date.now().toString(36)),
      version: 'v2',
      flowMode: requestedFlowMode,
      consultationId: null,
      owner: { name: '' },
      belief: null,
      contact: { email: '', phone: '', area: null, viberOptIn: false, smsOptIn: false, marketingConsent: false },
      dogCount: 1,
      activeDogIndex: 0,
      dogs: [makeDog()],
      stepIndex: 0,
      history: [],          /* stack of prior stepIndex values for back() */
      maxStepReached: 0,
      result: null,         /* server ResultBundle */
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    var flow = [];
    rebuildFlow();

    /* ---- flow composition: owner → (dogs × dog-screens) → results ---- */
    function rebuildFlow() {
      var prev = flow[state.stepIndex];
      flow = [];
      var ownerScreens = SCREENS.OWNER_SCREENS;
      var dogScreens = SCREENS.DOG_SCREENS;
      var resultScreens = SCREENS.RESULT_SCREENS;
      if (state.flowMode === 'ips2026') {
        var ipsDogIds = ['age', 'neutered', 'weight', 'sensitivities', 'activity', 'weightGoal'];
        var ipsResultIds = ['contact', 'plan', 'review'];
        ownerScreens = ownerScreens.filter(function (s) { return s.id === 'welcome'; });
        dogScreens = dogScreens.filter(function (s) { return ipsDogIds.indexOf(s.id) >= 0; });
        resultScreens = resultScreens.filter(function (s) { return ipsResultIds.indexOf(s.id) >= 0; });
      }
      ownerScreens.forEach(function (s) { flow.push({ screenId: s.id, dogIndex: null, chapter: s.chapter }); });
      for (var i = 0; i < state.dogCount; i++) {
        /* dogs added mid-flow ("+ Add another dog") never passed the welcome
           mad-lib, so they get their own name screen before the questions */
        if (state.dogs[i] && state.dogs[i].addedLater) flow.push({ screenId: 'dogname', dogIndex: i, chapter: 'basics' });
        dogScreens.forEach(function (s) { flow.push({ screenId: s.id, dogIndex: i, chapter: s.chapter }); });
      }
      resultScreens.forEach(function (s) { flow.push({ screenId: s.id, dogIndex: null, chapter: s.chapter }); });
      /* keep position stable across dogCount changes */
      if (prev) {
        var idx = indexOfStep(prev.screenId, prev.dogIndex);
        if (idx >= 0) state.stepIndex = idx;
      }
      if (state.stepIndex >= flow.length) state.stepIndex = flow.length - 1;
    }
    function indexOfStep(screenId, dogIndex) {
      for (var i = 0; i < flow.length; i++) {
        if (flow[i].screenId === screenId && flow[i].dogIndex === dogIndex) return i;
      }
      return -1;
    }

    /* ---- dog count management ---- */
    function setDogCount(n) {
      n = Math.max(1, Math.min(10, parseInt(n, 10) || 1));
      while (state.dogs.length < n) state.dogs.push(makeDog());
      if (state.dogs.length > n) state.dogs = state.dogs.slice(0, n);
      state.dogCount = n;
      rebuildFlow();
    }
    /* add a dog and jump to its NAME screen first — a mid-flow dog never passed
       the welcome mad-lib, and an unnamed dog renders as "your dog" on the
       reveal, pouches and checkout */
    function addDog() {
      if (state.dogs.length >= 10) return -1;
      var d = makeDog();
      d.addedLater = true;
      state.dogs.push(d);
      state.dogCount = state.dogs.length;
      rebuildFlow();
      var idx = state.dogs.length - 1;
      var target = indexOfStep('dogname', idx);
      if (target < 0) target = indexOfStep(SCREENS.DOG_SCREENS[0].id, idx);
      if (target >= 0) { state.stepIndex = target; if (target > state.maxStepReached) state.maxStepReached = target; }
      touch(); emit();
      return idx;
    }
    /* remove a dog (never below 1) and clear its recipe override upstream */
    function removeDog(i) {
      if (state.dogs.length <= 1 || i < 0 || i >= state.dogs.length) return false;
      state.dogs.splice(i, 1);
      state.dogCount = state.dogs.length;
      rebuildFlow();
      if (state.stepIndex >= flow.length) state.stepIndex = flow.length - 1;
      touch(); emit();
      return true;
    }

    /* ---- token context for copy ---- */
    function tokens(dogIndex) {
      var di = (dogIndex == null) ? activeDogIndex() : dogIndex;
      var dog = state.dogs[di] || state.dogs[0];
      var p = pronounSet(dog && dog.sex);
      var Name = dog && dog.name ? cap(dog.name) : 'your dog';
      var useName = state.flowMode === 'ips2026' && !(dog && dog.sex);
      var subject = useName ? Name : p.subj;
      var object = useName ? Name : p.obj;
      var possToken = useName
        ? (dog && dog.name ? possessive(Name) : 'your dog’s')
        : p.poss;
      return {
        Name: Name, name: dog && dog.name ? dog.name : 'your dog',
        NamePoss: dog && dog.name ? possessive(cap(dog.name)) : 'your dog’s',
        subj: subject, Subj: cap(subject),
        obj: object, Obj: cap(object),
        poss: possToken, Poss: cap(possToken),
        neuterTerm: p.neuter, neuterTermCap: cap(p.neuter),
        owner: state.owner.name ? cap(state.owner.name) : ''
      };
    }
    function activeDogIndex() {
      var step = flow[state.stepIndex];
      return (step && step.dogIndex != null) ? step.dogIndex : state.activeDogIndex;
    }

    /* Resolve a registry text field (string | fn(ctx) | "data:KEY"). */
    function resolveText(field, dogIndex) {
      if (field == null) return '';
      if (typeof field === 'string') {
        if (field.indexOf('data:') === 0) return DATA.COPY[field.slice(5)] || '';
        return t(field, dogIndex);
      }
      if (typeof field === 'function') {
        return field(ctx(dogIndex));
      }
      return '';
    }
    function ctx(dogIndex) {
      var di = (dogIndex == null) ? activeDogIndex() : dogIndex;
      return {
        t: function (tpl) { return t(tpl, di); },
        tokens: tokens(di),
        dog: state.dogs[di],
        owner: state.owner,
        answers: dogAnswersView(di),
        state: state,
        multi: state.dogCount > 1,
        data: DATA
      };
    }
    function dogAnswersView(di) {
      var d = state.dogs[di] || {};
      return { currentFoodLabel: d.currentFoodLabel || 'food' };
    }
    /* token templating: {Name}, {subj}, {poss}, … — escapes injected values */
    function t(tpl, dogIndex) {
      var tk = tokens(dogIndex);
      return String(tpl).replace(/\{(\w+)\}/g, function (_, k) {
        return Object.prototype.hasOwnProperty.call(tk, k) ? escapeHtml(tk[k]) : '';
      });
    }

    /* ---- recipe recommendation (mirrors backend computeReveal logic) ---- */
    /* Under 1 year = puppy. Adult-only recipes (Braised Beef Bowl / Hearty
       Pork Rice are adult-maintenance formulations) must never be recommended
       or pickable for a puppy. */
    function isPuppy(dog) {
      dog = dog || state.dogs[activeDogIndex()] || {};
      return (parseInt(dog.ageYears, 10) || 0) < 1;
    }
    function recipeSafeForDog(dog, key) {
      dog = dog || state.dogs[activeDogIndex()] || {};
      var r = DATA.RECIPES[key] || {};
      if (r.adultOnly && isPuppy(dog)) return false;
      var excluded = [].concat(dog.excludedAllergens || [], dog.avoidPreferences || []);
      var al = r.allergens || [];
      for (var j = 0; j < al.length; j++) { if (excluded.indexOf(al[j]) >= 0) return false; }
      return true;
    }
    function recommendRecipe(dog) {
      dog = dog || state.dogs[activeDogIndex()];
      var signals = [].concat(dog.sensitivities || [], dog.healthGoals || []);
      if (dog.weightGoal) signals.push(dog.weightGoal);
      var pick = DATA.DEFAULT_RECIPE;
      for (var i = 0; i < signals.length; i++) {
        if (DATA.HEALTH_RECIPE_MAP[signals[i]]) { pick = DATA.HEALTH_RECIPE_MAP[signals[i]]; break; }
      }
      function safe(key) { return recipeSafeForDog(dog, key); }
      if (safe(pick)) return pick;
      if (safe(DATA.DEFAULT_RECIPE)) return DATA.DEFAULT_RECIPE;
      for (var k = 0; k < DATA.RECIPE_ORDER.length; k++) { if (safe(DATA.RECIPE_ORDER[k])) return DATA.RECIPE_ORDER[k]; }
      /* bug-hunt P0-3: NO safe recipe (e.g. chicken-allergic puppy — everything
         conflicts or is adult-only). Never fall back to chicken and ship the
         allergen; return null so the UI shows a "let's build a custom plan" branch. */
      return null;
    }

    /* Variety plan per dog. One refused flavor must never read as "refuses
       McDuffy". Order: the health-guided pick leads, then the default variety
       pool and full menu fill the mix. Every safe recipe is eligible. */
    function recommendRecipes(dog) {
      dog = dog || state.dogs[activeDogIndex()];
      function safe(key) { return recipeSafeForDog(dog, key); }
      var want = DATA.RECOMMENDED_RECIPE_COUNT || 4;
      var mix = [];
      function push(key) {
        if (!key || mix.indexOf(key) >= 0 || !safe(key) || mix.length >= want) return;
        mix.push(key);
      }
      push(recommendRecipe(dog));
      (DATA.DEFAULT_RECIPE_MIX || []).forEach(function (k) { push(k); });
      (DATA.RECIPE_ORDER || []).forEach(function (k) { push(k); }); /* top up from the full menu if allergies shrank the pool */
      /* bug-hunt P0-3: mix is all-safe (push() guards) or empty — never fabricate
         a fallback that could be the allergen. Empty ⇒ caller shows the no-safe branch. */
      return mix;
    }

    /* ---- weight estimate from breed/size ---- */
    function estimateWeightKg(dog) {
      dog = dog || state.dogs[activeDogIndex()];
      var b = dog.breed || {};
      if (b.type === 'unknown' && b.sizeFallback) return DATA.SIZE_EST_KG[b.sizeFallback] || 16;
      var first = (b.breeds && b.breeds.filter(Boolean)[0]) || null;
      if (first) {
        var rec = findBreed(first.id);
        if (rec) return rec.estKg;
      }
      return 16;
    }
    function findBreed(id) {
      for (var i = 0; i < DATA.BREEDS.length; i++) { if (DATA.BREEDS[i].id === id) return DATA.BREEDS[i]; }
      return null;
    }

    /* ---- normalize weight to kg ---- */
    function weightKg(dog) {
      dog = dog || state.dogs[activeDogIndex()];
      if (dog.weight == null) return null;
      var w = parseFloat(dog.weight);
      if (isNaN(w)) return null;
      return dog.weightUnit === 'lbs' ? Math.round(w * LB_TO_KG * 10) / 10 : w;
    }

    /* ---- validation per step ---- */
    function validate(stepIndex) {
      var step = flow[stepIndex == null ? state.stepIndex : stepIndex];
      if (!step) return { ok: false, error: 'No step' };
      var id = step.screenId;
      var di = step.dogIndex;
      var d = di != null ? state.dogs[di] : null;

      switch (id) {
        case 'welcome':
          for (var i = 0; i < state.dogCount; i++) {
            if (!state.dogs[i].name || !state.dogs[i].name.trim()) return { ok: false, error: 'Please name every dog.' };
          }
          return { ok: true };
        case 'belief':
          return { ok: true }; /* optional yes-ladder — never blocks (2026-07-03) */
        case 'contact':
          if (!EMAIL_RE.test(state.contact.email || '')) return { ok: false, error: 'Please enter a valid email.' };
          if (!state.contact.area) return { ok: false, error: 'Choose your area so we can check delivery.' };
          /* Delivery gate: a non-serviceable area must NOT walk forward to
             checkout and pay for an order we can't fulfil. Their email is
             captured (waitlist); the reveal/checkout stays blocked. */
          var _area = (DATA.PH_AREAS || []).filter(function (x) { return x.value === state.contact.area; })[0];
          if (_area && _area.serviceable === false) {
            return { ok: false, error: 'We’re not delivering to ' + _area.label + ' just yet — your email is saved and we’ll message you the moment we are.' };
          }
          return { ok: true };
        case 'dogname':
          if (!d.name || !d.name.trim()) return { ok: false, error: 'What should we call your pup?' };
          if (d.name.length > 20) return { ok: false, error: 'Keep the name under 20 characters.' };
          return { ok: true };
        case 'sex':
          return d.sex ? { ok: true } : { ok: false, error: 'Boy or girl?' };
        case 'age':
          if (d.ageYears == null || isNaN(parseInt(d.ageYears, 10))) return { ok: false, error: 'Add an age in years.' };
          if (d.ageYears < 0 || d.ageYears > 30) return { ok: false, error: 'Years must be 0–30.' };
          if (d.ageMonths != null && (d.ageMonths < 0 || d.ageMonths > 11)) return { ok: false, error: 'Months must be 0–11.' };
          /* a 0y0m dog is invalid (matches backend; else the step silently fails server-side) */
          if ((parseInt(d.ageYears, 10) || 0) === 0 && (parseInt(d.ageMonths, 10) || 0) === 0) return { ok: false, error: 'Please add an age.' };
          return { ok: true };
        case 'neutered':
          return d.neutered ? { ok: true } : { ok: false, error: 'Pick one.' };
        case 'breed':
          if (!d.breed.type) return { ok: false, error: 'Pick pure, cross or not sure.' };
          if (d.breed.type === 'pure') return (d.breed.breeds.filter(Boolean).length >= 1) ? { ok: true } : { ok: false, error: 'Pick ' + tokens(di).NamePoss + ' breed from the list.' };
          if (d.breed.type === 'mixed') return (d.breed.breeds.filter(Boolean).length >= 1) ? { ok: true } : { ok: false, error: 'Pick at least one breed from the list.' };
          if (d.breed.type === 'unknown') return d.breed.sizeFallback ? { ok: true } : { ok: false, error: 'Pick a size so we can size portions.' };
          return { ok: false, error: 'Pick a breed option.' };
        case 'weight':
          var wk = weightKg(d);
          if (wk == null) return { ok: false, error: 'Add a weight (or use our estimate).' };
          if (wk < 0.1 || wk > 120) return { ok: false, error: 'That weight looks off — please check.' };
          return { ok: true };
        case 'activity':
          return d.activityTier ? { ok: true } : { ok: false, error: 'Slide to pick an activity level.' };
        case 'weightGoal':
          return d.weightGoal ? { ok: true } : { ok: false, error: 'Pick a goal.' };
        case 'currentFood':
          return d.currentFood ? { ok: true } : { ok: false, error: 'Pick what they eat now.' };
        /* optional / skippable steps always pass */
        default:
          return { ok: true };
      }
    }

    /* ---- answered? (for progress %) ---- */
    function isAnswered(step) {
      var id = step.screenId, di = step.dogIndex, d = di != null ? state.dogs[di] : null;
      switch (id) {
        case 'welcome': return state.dogs.every(function (x) { return !!(x.name && x.name.trim()); });
        case 'belief': return !!state.belief;
        case 'contact': return EMAIL_RE.test(state.contact.email || '') && !!state.contact.area;
        case 'dogname': return !!(d.name && d.name.trim());
        case 'sex': return !!d.sex;
        case 'age': return d.ageYears != null;
        case 'neutered': return !!d.neutered;
        case 'breed': return validate(indexOfStep('breed', di)).ok;
        case 'weight': return weightKg(d) != null;
        case 'activity': return !!d.activityTier;
        case 'weightGoal': return !!d.weightGoal;
        case 'currentFood': return !!d.currentFood;
        default: return false; /* optional steps don't gate progress */
      }
    }
    function isRequired(step) {
      var s = SCREENS.BY_ID[step.screenId];
      return s && s.required === true;
    }

    function progress() {
      var total = 0, done = 0;
      flow.forEach(function (step) {
        if (isRequired(step)) { total++; if (isAnswered(step)) done++; }
      });
      if (!total) return 0;
      return Math.round((done / total) * 100);
    }

    /* ---- navigation ---- */
    function currentStep() { return flow[state.stepIndex]; }
    function currentScreen() {
      var step = currentStep();
      return step ? SCREENS.BY_ID[step.screenId] : null;
    }
    function canAdvance() { return validate().ok; }

    function next() {
      var v = validate();
      if (!v.ok) return { ok: false, error: v.error };
      if (state.stepIndex < flow.length - 1) {
        state.history.push(state.stepIndex);
        state.stepIndex++;
        state.maxStepReached = Math.max(state.maxStepReached, state.stepIndex);
        touch(); emit();
      }
      return { ok: true };
    }
    function back() {
      if (state.history.length) {
        state.stepIndex = state.history.pop();
        touch(); emit();
        return { ok: true };
      }
      if (state.stepIndex > 0) { state.stepIndex--; touch(); emit(); return { ok: true }; }
      return { ok: false };
    }
    function gotoScreen(screenId, dogIndex) {
      var idx = (dogIndex === undefined) ? firstIndexOfScreen(screenId) : indexOfStep(screenId, dogIndex);
      if (idx < 0) return { ok: false };
      state.history.push(state.stepIndex);
      state.stepIndex = idx;
      touch(); emit();
      return { ok: true };
    }
    function firstIndexOfScreen(screenId) {
      for (var i = 0; i < flow.length; i++) { if (flow[i].screenId === screenId) return i; }
      return -1;
    }

    /* ---- answer setters ---- */
    function setOwnerName(v) { state.owner.name = String(v || '').slice(0, 30); touch(); emit(); }
    function setDogName(di, v) { if (state.dogs[di]) { state.dogs[di].name = String(v || '').slice(0, 20); touch(); emit(); } }
    function setBelief(v) { state.belief = v; touch(); emit(); }
    function setContact(patch) { Object.assign(state.contact, patch || {}); touch(); emit(); }

    function setDogField(di, key, value) {
      if (!state.dogs[di]) return;
      state.dogs[di][key] = value;
      if (key === 'sex') { /* lexicon derives from sex — nothing else to store */ }
      if (key === 'currentFood') {
        var f = DATA.CURRENT_FOODS.filter(function (o) { return o.value === value; })[0];
        state.dogs[di].currentFoodLabel = f ? f.answerLabel : 'food';
      }
      touch(); emit();
    }
    function setBreed(di, patch) { Object.assign(state.dogs[di].breed, patch || {}); touch(); emit(); }
    function toggleInArray(di, key, value, exclusiveValues) {
      var arr = state.dogs[di][key] || [];
      var idx = arr.indexOf(value);
      if (idx >= 0) arr.splice(idx, 1);
      else {
        /* exclusive option (e.g. "None of these") clears the rest */
        if (exclusiveValues && exclusiveValues.indexOf(value) >= 0) arr = [value];
        else arr = arr.filter(function (x) { return !exclusiveValues || exclusiveValues.indexOf(x) < 0; }).concat(value);
      }
      state.dogs[di][key] = arr;
      touch(); emit();
    }

    /* ---- summary translation (plan-language, not raw echoes) ---- */
    function summaryFor(di) {
      var d = state.dogs[di], tk = tokens(di), out = { basics: [], health: [], lifestyle: [], meals: [] };
      if (d.sex) out.basics.push(d.sex === 'boy' ? 'Boy' : 'Girl');
      if (d.ageYears != null) out.basics.push(d.ageYears + (d.ageMonths ? ' yr ' + d.ageMonths + ' mo' : ' yrs'));
      if (d.neutered) out.basics.push(d.neutered === 'yes' ? cap(tk.neuterTerm) : (d.neutered === 'no' ? 'Not ' + tk.neuterTerm : tk.neuterTermCap + '?'));
      var bl = breedLabel(d); if (bl) out.basics.push(bl);
      if (weightKg(d) != null) out.basics.push(weightKg(d) + ' kg' + (d.weightEstimated ? ' (est.)' : ''));
      (d.excludedAllergens || []).forEach(function (a) { out.health.push('Excludes ' + a); });
      (d.healthGoals || []).forEach(function (g) {
        var go = DATA.HEALTH_GOALS.filter(function (x) { return x.value === g; })[0];
        if (go) out.health.push('Focus: ' + go.label.toLowerCase());
      });
      if (d.activityTier) { var at = DATA.ACTIVITY_TIERS.filter(function (x) { return x.value === d.activityTier; })[0]; if (at) out.lifestyle.push(at.label); }
      if (d.weightGoal) { var wg = DATA.WEIGHT_GOALS.filter(function (x) { return x.value === d.weightGoal; })[0]; if (wg) out.lifestyle.push(wg.label); }
      if (d.currentFood) { var cf = DATA.CURRENT_FOODS.filter(function (x) { return x.value === d.currentFood; })[0]; if (cf) out.meals.push('Eats ' + cf.answerLabel); }
      return out;
    }
    function breedLabel(d) {
      var b = d.breed || {};
      if (b.type === 'unknown') return b.sizeFallback ? (cap(b.sizeFallback) + ' dog') : '';
      var picked = (b.breeds || []).filter(Boolean);
      if (picked.length) return picked.map(function (x) { return x.label; }).join(' × ');
      return '';
    }

    /* ---- snapshot for persistence / server ---- */
    function snapshot() {
      return JSON.parse(JSON.stringify({
        sessionId: state.sessionId, version: state.version, flowMode: state.flowMode, consultationId: state.consultationId,
        owner: state.owner, belief: state.belief, contact: state.contact,
        dogCount: state.dogCount, activeDogIndex: activeDogIndex(),
        dogs: state.dogs, stepIndex: state.stepIndex, maxStepReached: state.maxStepReached,
        currentScreenId: currentStep() ? currentStep().screenId : null,
        createdAt: state.createdAt, updatedAt: state.updatedAt
      }));
    }
    function hydrate(snap) {
      if (!snap || typeof snap !== 'object') return;
      try {
        if (snap.sessionId) state.sessionId = snap.sessionId;
        if (snap.consultationId) state.consultationId = snap.consultationId;
        if (snap.owner) state.owner = snap.owner;
        if (snap.belief) state.belief = snap.belief;
        if (snap.contact) state.contact = Object.assign(state.contact, snap.contact);
        if (Array.isArray(snap.dogs) && snap.dogs.length) {
          state.dogs = snap.dogs.map(function (d) { return Object.assign(makeDog(), d); });
          state.dogCount = state.dogs.length;
        }
        rebuildFlow();
        if (typeof snap.stepIndex === 'number') state.stepIndex = Math.min(Math.max(0, snap.stepIndex), flow.length - 1);
        /* restore furthest progress so chapter tabs stay clickable on resume */
        if (typeof snap.maxStepReached === 'number') state.maxStepReached = Math.min(Math.max(snap.maxStepReached, state.stepIndex), flow.length - 1);
        else state.maxStepReached = state.stepIndex;
        emit();
      } catch (e) { /* ignore corrupt snapshot */ }
    }

    /* ---- pub/sub ---- */
    function subscribe(fn) { subscribers.push(fn); return function () { subscribers = subscribers.filter(function (f) { return f !== fn; }); }; }
    function emit() { var snap = getState(); subscribers.forEach(function (fn) { try { fn(snap); } catch (e) { /* */ } }); }
    function touch() { state.updatedAt = new Date().toISOString(); }

    function getState() {
      var step = currentStep();
      return {
        state: state,
        step: step,
        screen: currentScreen(),
        chapter: step ? step.chapter : null,
        dogIndex: step ? step.dogIndex : null,
        progress: progress(),
        canAdvance: canAdvance(),
        isLast: state.stepIndex >= flow.length - 1,
        isFirst: state.stepIndex === 0,
        flowLength: flow.length,
        tokens: tokens(),
        ctx: ctx()
      };
    }

    /* ---- public API ---- */
    return {
      state: state,
      getState: getState,
      subscribe: subscribe,
      /* nav */
      next: next, back: back, gotoScreen: gotoScreen, validate: validate, progress: progress,
      currentScreen: currentScreen, currentStep: currentStep, flow: function () { return flow; },
      indexOfStep: indexOfStep, firstIndexOfScreen: firstIndexOfScreen,
      /* setters */
      setDogCount: setDogCount, addDog: addDog, removeDog: removeDog, setOwnerName: setOwnerName, setDogName: setDogName,
      setBelief: setBelief, setContact: setContact, setDogField: setDogField, setBreed: setBreed,
      toggleInArray: toggleInArray,
      /* derived */
      tokens: tokens, t: t, resolveText: resolveText, ctx: ctx,
      recommendRecipe: recommendRecipe, recommendRecipes: recommendRecipes, estimateWeightKg: estimateWeightKg, weightKg: weightKg,
      recipeSafeForDog: recipeSafeForDog, isPuppy: isPuppy,
      findBreed: findBreed, summaryFor: summaryFor, breedLabel: breedLabel,
      /* persistence */
      snapshot: snapshot, hydrate: hydrate,
      /* expose helpers for tests */
      _helpers: { escapeHtml: escapeHtml, cap: cap, possessive: possessive, pluralBags: pluralBags, pronounSet: pronounSet, EMAIL_RE: EMAIL_RE }
    };
  }

  return { createEngine: createEngine, makeDog: makeDog, _util: { escapeHtml: escapeHtml, cap: cap, pluralBags: pluralBags } };
});
